import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  PLATFORM_ID,
  SimpleChanges,
  ViewChild,
} from '@angular/core';

declare var $: any;

@Component({
  selector: 'app-angular-editor',
  templateUrl: './angular-editor.component.html',
  styleUrls: ['./angular-editor.component.css'],
})
export class AngularEditorComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  private static loadingPromise: Promise<void> | null = null;

  @ViewChild('editor', { static: false }) editor!: ElementRef;
  @Output() contentUpdated = new EventEmitter<string>();
  @Input() answerText!: string;
  @Input() isAnsweUploaded!: boolean;

  htmlContent = '';
  private trumbowygInitialized = false;

  constructor(@Inject(PLATFORM_ID) private platformId: any, @Inject(DOCUMENT) private document: Document) {}

  ngOnInit() {
    if (this.isAnsweUploaded === true) {
      this.clear();
      this.resetForum();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['isAnsweUploaded']?.currentValue === true) {
      this.clear();
      this.resetForum();
    }

    if (changes['answerText'] && this.trumbowygInitialized) {
      this.updateEditorContent(changes['answerText'].currentValue || '');
    }
  }

  async ngAfterViewInit() {
    if (AngularEditorComponent.loadingPromise) {
      await AngularEditorComponent.loadingPromise;
    } else {
      AngularEditorComponent.loadingPromise = this.loadCSStrumbowyg();
      await AngularEditorComponent.loadingPromise;
      AngularEditorComponent.loadingPromise = null;
    }

    this.initializeTrumbowyg();
  }

  ngOnDestroy() {
    if (this.trumbowygInitialized && this.editor) {
      this.detachEditorFocusBinding();
      $(this.editor.nativeElement).trumbowyg('destroy');
      this.trumbowygInitialized = false;
    }
  }

  private contentChanges() {
    this.contentUpdated.emit(this.htmlContent);
  }

  private async loadCSStrumbowyg() {
    await new Promise((resolve) => setTimeout(resolve, 1000));

    if (isPlatformBrowser(this.platformId) && typeof document !== 'undefined') {
      await this.loadScript('/assets/js/jquery-3.6.0.min.js');
      await this.loadScript('/assets/trumbowyg/trumbowyg.min.js');
      this.loadCSSFile('/assets/trumbowyg/trumbowyg.min.css');
    }
  }

  private async loadScript(src: string) {
    const existingScript = this.document.querySelector(`script[src="${src}"]`);
    if (existingScript) {
      return;
    }

    const script = this.document.createElement('script');
    script.src = src;
    script.type = 'text/javascript';
    script.async = true;

    await new Promise<void>((resolve, reject) => {
      script.onload = () => resolve();
      script.onerror = () => {
        console.error(`Error loading script: ${src}`);
        reject(`Error loading script: ${src}`);
      };

      this.document.head.appendChild(script);
    });
  }

  private loadCSSFile(href: string) {
    if (this.document.querySelector(`link[href="${href}"]`)) {
      return;
    }

    const link = this.document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    this.document.head.appendChild(link);
  }

  private initializeTrumbowyg() {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    if (!this.editor || !$.fn.trumbowyg) {
      console.error('Trumbowyg not initialized: jQuery or Trumbowyg missing');
      return;
    }

    const editorInstance = $(this.editor.nativeElement);

    if (this.trumbowygInitialized) {
      this.bindEditorFocus(editorInstance);
      return;
    }

    editorInstance
      .trumbowyg({
        btns: [
          ['viewHTML'],
          ['undo', 'redo'],
          ['formatting'],
          ['strong', 'em', 'del'],
          ['superscript', 'subscript'],
          ['justifyLeft', 'justifyCenter', 'justifyRight', 'justifyFull'],
          ['unorderedList', 'orderedList'],
          ['horizontalRule'],
          ['removeformat'],
          ['fullscreen'],
        ],
        svgPath: '/assets/trumbowyg/ui/icons.svg',
      })
      .on('tbwinit', () => {
        this.trumbowygInitialized = true;
        this.updateEditorContent(this.answerText || this.htmlContent || '');
        this.bindEditorFocus(editorInstance);
      })
      .on('tbwchange tbwblur tbwpaste', () => {
        this.htmlContent = editorInstance.trumbowyg('html');
        this.contentChanges();
      });
  }

  private updateEditorContent(content: string) {
    this.htmlContent = content || '';

    if (this.trumbowygInitialized) {
      $(this.editor.nativeElement).trumbowyg('html', this.htmlContent);
    }
  }

  private bindEditorFocus(editorInstance: any) {
    if (!this.editor) {
      return;
    }

    const wrapper = editorInstance.closest('.trumbowyg-box');
    const editorSurfaceBox = wrapper.find('.trumbowyg-editor-box');
    const editorSurface = wrapper.find('.trumbowyg-editor');

    if (!wrapper.length || !editorSurfaceBox.length || !editorSurface.length) {
      return;
    }

    editorSurfaceBox.css('cursor', 'text');
    editorSurfaceBox.off('mousedown.codexEditorFocus');
    editorSurfaceBox.on('mousedown.codexEditorFocus', (event: Event) => {
      const target = event.target as HTMLElement | null;
      if (target?.closest('.trumbowyg-editor')) {
        return;
      }

      event.preventDefault();
      const editorElement = editorSurface.get(0) as HTMLElement | undefined;
      if (!editorElement) {
        return;
      }

      editorElement.focus({ preventScroll: true });
      const selection = window.getSelection();
      const range = document.createRange();
      range.selectNodeContents(editorElement);
      range.collapse(false);
      selection?.removeAllRanges();
      selection?.addRange(range);
    });
  }

  private detachEditorFocusBinding() {
    if (!this.editor) {
      return;
    }

    const wrapper = $(this.editor.nativeElement).closest('.trumbowyg-box');
    wrapper.find('.trumbowyg-editor-box').off('mousedown.codexEditorFocus');
  }

  clear() {
    this.htmlContent = '';
    this.answerText = '';
    this.contentUpdated.emit('');
  }

  resetForum() {
    const fileInput = this.document.getElementsByName('files')[0] as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }

    const rootElement = this.document.querySelector('app-root');
    if (rootElement?.hasAttribute('aria-hidden')) {
      rootElement.removeAttribute('aria-hidden');
    }

    if (this.trumbowygInitialized) {
      $(this.editor.nativeElement).trumbowyg('empty');
    }

    this.contentUpdated.emit('');
  }
}
